import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ImageUpload } from 'apps/shared/scripts/__generated__/graphql.generated';
import { usePostById, useUpdatePost } from 'apps/shared/scripts/graphql';
import { useFormCallback, usePutSignedUrl } from 'apps/shared/scripts/hooks';
import { modalDataIdSelector } from 'apps/shared/scripts/redux/selectors';

import { CardPostForm, CardPostFormLoader } from '../../components';
import { FormValues } from '../../components/CardPostForm';
import { getInitialValues, toMediaInput } from '../../utils';

interface Props {
  closeModal: () => void;
}

const CardPostFormUpdateContainer = ({ closeModal }: Props) => {
  const client = useApolloClient();
  const postId = useSelector(modalDataIdSelector);
  const { mutate: updatePost, isMutating: isUpdating } = useUpdatePost();
  const {
    post,
    isLoading,
    error: fetchError,
  } = usePostById({ variables: { id: postId } });
  const { isSuccess, onSuccess, onError } = useFormCallback();
  const { isUploading, putSignedUrl } = usePutSignedUrl();

  const onSubmit = useCallback(
    async (data: FormValues) => {
      try {
        const { media } = data;
        if (media && media.file) {
          const { result: imageUpload, errors } = await updatePost({
            id: postId,
            input: {
              ...data,
              media: toMediaInput(media),
            },
          });
          if (!imageUpload || errors.length > 0) {
            throw new Error(errors[0].message);
          }
          const { signedUrl } = imageUpload as ImageUpload;
          await putSignedUrl(signedUrl, media.file);
        } else {
          const { result, errors } = await updatePost({
            id: postId,
            input: data,
          });
          if (!result || errors.length > 0) {
            throw new Error(errors[0].message);
          }
        }
        onSuccess({ message: 'Success! Post was updated' }, closeModal);
        await client.refetchQueries({
          include: ['PostsByCardId'],
        });
      } catch (e) {
        onError(e);
        throw e;
      }
    },
    [client, closeModal, onError, onSuccess, postId, putSignedUrl, updatePost],
  );

  useEffect(() => {
    if (fetchError) {
      onError({ message: fetchError }, closeModal);
    }
  }, [closeModal, fetchError, onError]);

  if (isLoading || !post) {
    return <CardPostFormLoader />;
  }

  return (
    <CardPostForm
      closeModal={closeModal}
      onSubmit={onSubmit}
      defaultValues={getInitialValues(post)}
      isSubmitting={isUpdating || isUploading || isSuccess}
      isEditMode
    />
  );
};

CardPostFormUpdateContainer.displayName = 'CardPostFormUpdateContainer';

export default CardPostFormUpdateContainer;
